/**
 * <kallo-survey> — walks a form one step at a time.
 *
 * The markup renders every step visible, so with this script blocked the whole
 * questionnaire is still fillable and submittable. Progressive enhancement only
 * hides the steps once it can also offer a way back to them.
 */
(function () {
  'use strict';

  class KalloSurvey extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.steps = Array.from(this.querySelectorAll('[data-survey-step]'));
      this.back = this.querySelector('[data-survey-back]');
      this.next = this.querySelector('[data-survey-next]');
      this.submit = this.querySelector('[data-survey-submit]');

      // One step is just a plain form; leave it exactly as rendered.
      if (this.steps.length < 2) return;

      this.index = 0;
      this.counter = this.buildCounter();

      this.next?.addEventListener('click', () => this.advance());
      this.back?.addEventListener('click', () => this.retreat());

      // Enter inside a field should move on rather than submit a half-finished
      // questionnaire from step one.
      this.form?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        if (event.target.tagName === 'TEXTAREA') return;
        if (this.index < this.steps.length - 1) {
          event.preventDefault();
          this.advance();
        }
      });

      this.show(0, { focus: false });
    }

    /** A "Step 1 of 3" line, announced politely as the visitor moves. */
    buildCounter() {
      const template = this.dataset.stepLabels || '';
      if (!template) return null;
      const el = document.createElement('p');
      el.className = 'survey__counter';
      el.setAttribute('role', 'status');
      this.form?.prepend(el);
      return el;
    }

    updateCounter() {
      if (!this.counter) return;
      this.counter.textContent = (this.dataset.stepLabels || '')
        .replace('[current]', String(this.index + 1))
        .replace('[total]', String(this.steps.length));
    }

    /**
     * Only validates the step being left, so a required answer further down the
     * form does not block progress before the visitor has reached it.
     */
    stepIsValid(step) {
      const fields = Array.from(step.querySelectorAll('input, select, textarea'));
      for (const field of fields) {
        if (!field.checkValidity()) {
          field.reportValidity();
          return false;
        }
      }
      return true;
    }

    advance() {
      const current = this.steps[this.index];
      if (current && !this.stepIsValid(current)) return;
      if (this.index < this.steps.length - 1) this.show(this.index + 1);
    }

    retreat() {
      if (this.index > 0) this.show(this.index - 1);
    }

    show(index, options) {
      const focus = !options || options.focus !== false;
      this.index = index;

      this.steps.forEach((step, i) => {
        step.hidden = i !== index;
      });

      const isLast = index === this.steps.length - 1;
      if (this.back) this.back.hidden = index === 0;
      if (this.next) this.next.hidden = isLast;
      if (this.submit) this.submit.hidden = !isLast;

      this.updateCounter();

      if (focus) {
        // Move the reader to the top of the new step rather than leaving focus
        // on a button that has just moved or disappeared.
        const step = this.steps[index];
        const target = step.querySelector('input, select, textarea');
        if (target) target.focus({ preventScroll: true });
        step.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }

  if (!customElements.get('kallo-survey')) {
    customElements.define('kallo-survey', KalloSurvey);
  }
})();
